"use client";

import { useState } from "react";
import { Lock, Eye, Phone, EyeClosed } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";import { useRouter } from "next/navigation";

import Image from "next/image";
import zayverseLogo from "@/public/image/Zayverse Icon.png";
import { useGlobal } from "../contexts/globalcontext";
import ResetPasswordModal from "./resetpasswordmodal";

export default function LoginPage() {
  const { setUserData, setMerchantData, setToken } = useGlobal();

  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const router = useRouter();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const res = await fetch("/api/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json" },
        body: JSON.stringify({ phone, password, rememberToken: rememberMe }),
        signal: AbortSignal.timeout(30000) });

      const data = await res.json();

      if (!res.ok || data.error || !data.token) {
        setError(data.error || `Login failed (${res.status})`);
        return;
      }

      setToken(data.token);
      setUserData(data.user);
      setMerchantData(data.merchant);
      router.replace("/dashboard/landing");
    } catch (err) {
      console.error("Login error:", err);
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-white dark:bg-black p-4">
      {/* Login Card */}
      <Card className="w-full max-w-md shadow-xl rounded-lg border border-gray-200 dark:bg-black dark:border-gray-700">
        <CardHeader className="space-y-4 p-6">
          <div className="flex flex-col items-center justify-center w-full">
            <Image
              src={zayverseLogo}
              alt="profile"
              className="shadow-lg rounded-full h-60 w-60 bg-gray-50 object-cover hover:border hover:border-gray-50 transition duration-300 ease-in-out"
            />

            <CardTitle className="text-xl play-fair py-3 font-bold text-gray-900 dark:text-white text-center">
              Welcome to Zayverse
            </CardTitle>
          </div>
          <span className="text-center text-gray-600 dark:text-gray-400">
            Please enter your credentials to access your account
          </span>
        </CardHeader>

        <CardContent className="p-8 pt-0">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="phone" className="newsreader-font font-bold">
                  Phone
                </Label>
                <div className="relative flex items-center">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="Enter your phone number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="h-11 pl-10 w-full outline-none bg-white dark:bg-black text-black dark:text-white border border-gray-300 dark:border-gray-700 rounded-lg"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="newsreader-font font-bold">
                  Password
                </Label>
                <div className="relative flex items-center">
                  <Lock className="absolute left-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 outline-none h-11 w-full bg-white dark:bg-black text-black dark:text-white border border-gray-300 dark:border-gray-700 rounded-lg"
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    className="absolute right-3 text-gray-400 hover:text-black dark:hover:text-white"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {password ? (
                      showPassword ? (
                        <Eye className="h-5 w-5" />
                      ) : (
                        <EyeClosed className="h-5 w-5" />
                      )
                    ) : null}
                  </button>
                </div>
                {error && (
                  <div className="mt-2 font-medium text-destructive text-red-500">
                    {error}
                  </div>
                )}
              </div>

              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mt-4">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    className="accent-secondary-color h-5 w-5 cursor-pointer"
                    onChange={() => setRememberMe(!rememberMe)}
                  />
                  <span className="dark:text-gray-200 font-medium select-none hover:text-secondary-color text-primary-color dark:hover:text-blue-400 transition-colors duration-200">
                    Remember me
                  </span>
                </label>

                {/* Updated Forgot Password Link to open modal */}
                <button
                  type="button"
                  onClick={() => setShowResetModal(true)}
                  className="mt-3 sm:mt-0 text-primary-color dark:text-blue-400 font-medium hover:underline hover:text-secondary-color dark:hover:text-blue-300 transition-colors duration-200"
                >
                  Forgot password?
                </button>
              </div>
            </div>

            <button
              className="custom-darkblue-button custom-shadow-effect w-full play-fair"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Signing in...
                </div>
              ) : (
                "Sign in"
              )}
            </button>
          </form>
        </CardContent>
      </Card>

      {/* Reset Password Modal */}
      <ResetPasswordModal
        isOpen={showResetModal}
        onClose={() => setShowResetModal(false)}
        onSuccess={() => {
          // Optional: Show a success toast or notification
          console.log("Password reset successful!");
        }}
      />
    </div>
  );
}
